/**
 * Presents a failure the way the constitution requires: the plain Australian
 * English message first, the technical payload only behind a disclosure the
 * user chooses to open (Principle IV, FR-059, SC-006).
 *
 * @author John Grimes
 */

import { AlertIcon } from "@primer/octicons-react";

import type { JSX, ReactNode } from "react";
import type { PlainError as PlainErrorBody } from "shared/api";

/** How prominent the failure is. */
export type PlainErrorVariant = "error" | "warning" | "info";

/** Props for {@link PlainError}. */
export type PlainErrorProps = {
  /** The plain message and, where there is one, the technical payload. */
  error: PlainErrorBody;
  /** Severity; defaults to `error`. Degraded states are warnings. */
  variant?: PlainErrorVariant;
  /** Buttons offering the next action, where one exists (Principle V). */
  actions?: ReactNode;
  /** Extra classes for the alert, for layout at the call site. */
  className?: string;
};

/** daisyUI alert class for each severity. */
const ALERT_CLASS: Record<PlainErrorVariant, string> = {
  error: "alert-error",
  warning: "alert-warning",
  info: "alert-info",
};

/**
 * Renders a failure as a soft alert with an optional technical disclosure.
 *
 * @param props - The component's props.
 * @param props.error - The plain message and technical payload.
 * @param props.variant - How prominent the failure is.
 * @param props.actions - Buttons offering the next action.
 * @param props.className - Extra classes for the alert.
 * @returns The alert element.
 *
 * @example
 * ```tsx
 * <PlainError
 *   error={{ message: "The preview couldn't run.", detail: raw }}
 *   actions={<button className="btn btn-primary">Try again</button>}
 * />
 * ```
 */
export const PlainError = ({
  error,
  variant = "error",
  actions,
  className = "",
}: Readonly<PlainErrorProps>): JSX.Element => (
  <div
    role="alert"
    className={`alert ${ALERT_CLASS[variant]} alert-soft text-sm text-left flex flex-col items-stretch gap-2 ${className}`}
  >
    <div className="flex items-start gap-2">
      <AlertIcon size={16} className="shrink-0 mt-0.5" aria-hidden />
      <span className="min-w-0">{error.message}</span>
    </div>
    {error.detail !== undefined && (
      // The disclosure sits on the alert's own tinted colour, which is already
      // mixed towards the theme's content colour, so dimming it further would
      // take it under 4.5:1. The smaller type and the monospace payload mark
      // it as secondary instead (SC-005, SC-006).
      <details className="text-sm min-w-0">
        <summary>Technical details</summary>
        <pre className="font-mono whitespace-pre-wrap break-words m-0 mt-1.5 max-h-64 overflow-auto">
          {error.detail}
        </pre>
      </details>
    )}
    {actions !== undefined && (
      <div className="flex flex-wrap gap-2 mt-2">{actions}</div>
    )}
  </div>
);
